export interface TokenPayload {
  token: string;
  tokenHash: string;
  generatedAt: number;
  expiresAt: number;
}

export class TokenService {
  private static readonly TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly TOKEN_LENGTH = 32;

  static generateToken(): TokenPayload {
    const token = this.generateReadableToken();
    const generatedAt = Date.now();
    const expiresAt = generatedAt + this.TOKEN_EXPIRY_MS;
    const tokenHash = this.hashToken(token);

    return {
      token,
      tokenHash,
      generatedAt,
      expiresAt,
    };
  }

  static generateNewAccessToken(): string {
    return this.generateReadableToken();
  }

  private static generateReadableToken(): string {
    // Generate a readable token with format: XXXXXXXX-XXXX-XXXX-XXXX
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let token = '';

    // First part: 8 chars
    for (let i = 0; i < 8; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    token += '-';

    // Second part: 4 chars
    for (let i = 0; i < 4; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    token += '-';

    // Third part: 4 chars
    for (let i = 0; i < 4; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    token += '-';

    // Fourth part: 4 chars
    for (let i = 0; i < 4; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return token;
  }

  static hashToken(token: string): string {
    // Use a simple but consistent hash function
    let hash = 0;
    const str = token.toUpperCase();

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    // Convert to hex and ensure consistent length
    const hex = (hash >>> 0).toString(16); // >>> 0 to handle negative numbers
    return 'hash_' + hex.padStart(8, '0');
  }

  static validateToken(tokenHash: string, expiresAt: number): boolean {
    const now = Date.now();
    return expiresAt > now;
  }

  static getRemainingTime(expiresAt: number): { hours: number; minutes: number; isExpired: boolean } {
    const now = Date.now();
    const remaining = expiresAt - now;

    if (remaining <= 0) {
      return { hours: 0, minutes: 0, isExpired: true };
    }

    const totalMinutes = Math.floor(remaining / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes, isExpired: false };
  }

  static formatRemainingTime(hours: number, minutes: number): string {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
}
